'use client';

import { RefObject, useMemo } from 'react';
import { trailingThrottle } from '@benzinga/utils';
import { isNearScrollEnd } from './usePagedFetch';
import { useEventListener } from './useEventListener';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

export function useNearEndScroll<T extends HTMLElement>(
  elementRef: RefObject<T | null>,
  {
    enabled = true,
    onNearEnd,
    thresholdPx = 240,
  }: {
    enabled?: boolean;
    onNearEnd?: () => void;
    thresholdPx?: number;
  },
) {
  const checkNearEnd = useMemo(
    () =>
      trailingThrottle(() => {
        if (!enabled || !onNearEnd) return;
        const element = elementRef.current;
        if (!element) return;
        if (isNearScrollEnd(element, { thresholdPx })) {
          onNearEnd();
        }
      }, 100),
    [elementRef, enabled, onNearEnd, thresholdPx],
  );

  useEventListener('scroll', checkNearEnd, elementRef as RefObject<T>, { passive: true });
  useEventListener('resize', checkNearEnd, elementRef as RefObject<T>, { passive: true });

  useIsomorphicLayoutEffect(() => {
    checkNearEnd();
  }, [checkNearEnd]);
}
